import { useState, useEffect } from 'react';
import { Download, Loader2, AlertCircle, Check, X } from 'lucide-react';
import { ref, getDownloadURL } from 'firebase/storage';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import VideoPlayer from '@/components/video/VideoPlayer';
import { storage } from '@/lib/firebase';
import { cn } from '@/lib/utils';
import type { Clip, ReviewStatus } from '@/types/job';

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

interface ClipCardProps {
  clip: Clip;
  onReview?: (clipIndex: number, status: ReviewStatus) => void;
}

export default function ClipCard({ clip, onReview }: ClipCardProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const status = clip.reviewStatus || 'pending';

  useEffect(() => {
    if (!clip.path) {
      setError('No clip path');
      return;
    }
    const storageRef = ref(storage, clip.path);
    getDownloadURL(storageRef)
      .then((resolvedUrl) => {
        setUrl(resolvedUrl);
        setError(null);
      })
      .catch((err) => setError(err.code || err.message));
  }, [clip.path]);

  return (
    <Card
      className={cn(
        'transition-all',
        status === 'approved' && 'ring-2 ring-green-500 border-green-500',
        status === 'rejected' && 'opacity-50 ring-1 ring-destructive border-destructive',
      )}
    >
      <CardContent className="p-3 space-y-3">
        {url ? (
          <VideoPlayer src={url} className="aspect-video" />
        ) : error ? (
          <div className="aspect-video bg-muted rounded-lg flex flex-col items-center justify-center gap-2">
            <AlertCircle className="h-5 w-5 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">{error}</p>
          </div>
        ) : (
          <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Badge variant="secondary">{clip.label}</Badge>
            <p className="text-xs text-muted-foreground">
              {formatTime(clip.startTime)} - {formatTime(clip.endTime)} ({clip.duration.toFixed(1)}s)
            </p>
          </div>
          {url && (
            <Button variant="outline" size="icon" asChild>
              <a href={url} download={`clip_${clip.index}.mp4`}>
                <Download className="h-4 w-4" />
              </a>
            </Button>
          )}
        </div>

        {onReview && (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={status === 'approved' ? 'default' : 'outline'}
              className={cn('flex-1', status === 'approved' && 'bg-green-600 hover:bg-green-700')}
              onClick={() => onReview(clip.index, status === 'approved' ? 'pending' : 'approved')}
            >
              <Check className="h-4 w-4 mr-1" />
              {status === 'approved' ? 'Approved' : 'Approve'}
            </Button>
            <Button
              size="sm"
              variant={status === 'rejected' ? 'destructive' : 'outline'}
              className="flex-1"
              onClick={() => onReview(clip.index, status === 'rejected' ? 'pending' : 'rejected')}
            >
              <X className="h-4 w-4 mr-1" />
              {status === 'rejected' ? 'Rejected' : 'Reject'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
